import React, { useEffect } from "react";
import { WidgetWrapper } from "../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../components/EditPanel/types";
import { RewardPillWidgetProps, RewardPillWidgetState } from "./types";
import { useWidgetEditable } from "../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../utils/fontWeightOptions";
import { customColor, themeColor } from "../../types/color";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: RewardPillWidgetState = {
  // Instant reward (DailyDeals) - orange
  instantBgColor: customColor("rgba(236, 121, 46, 0.3)"),
  instantTextColor: customColor("rgb(236, 121, 46)"),

  // Purchase reward - magenta
  purchaseBgColor: customColor("rgba(162, 34, 114, 0.3)"),
  purchaseTextColor: customColor("rgb(162, 34, 114)"),

  // Affiliate reward - teal
  affiliateBgColor: customColor("rgba(22, 148, 155, 0.2)"),
  affiliateTextColor: customColor("rgb(22, 148, 155)"),

  // Content/Survey reward - magenta
  contentBgColor: customColor("rgba(162, 34, 114, 0.3)"),
  contentTextColor: customColor("rgb(162, 34, 114)"),

  // Pill styling
  pillBorderRadius: 25,
  pillPaddingMultiplier: 0.95,

  // Icon
  iconSizeMultiplier: 1,
  iconMarginMultiplier: 0.416,

  // Text
  pillFontWeight: "semibold",
  pillFontFamily: "inherit",
  subfixTextColor: themeColor("textPrimary"),
  subfixFontWeight: "semibold",
  subfixFontFamily: "inherit",

  // Container
  containerGap: 7,
};

const editFields: EditField[] = [
  // Instant reward
  { key: "instantBgColor", label: "Instant Reward Background", type: "color" },
  { key: "instantTextColor", label: "Instant Reward Text", type: "color" },

  // Purchase reward
  {
    key: "purchaseBgColor",
    label: "Purchase Reward Background",
    type: "color",
  },
  { key: "purchaseTextColor", label: "Purchase Reward Text", type: "color" },

  // Affiliate reward
  {
    key: "affiliateBgColor",
    label: "Affiliate Reward Background",
    type: "color",
  },
  { key: "affiliateTextColor", label: "Affiliate Reward Text", type: "color" },

  // Content/Survey reward
  { key: "contentBgColor", label: "Content Reward Background", type: "color" },
  { key: "contentTextColor", label: "Content Reward Text", type: "color" },

  // Pill styling
  {
    key: "pillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 0,
    max: 50,
    step: 2,
  },
  {
    key: "pillPaddingMultiplier",
    label: "Pill Padding Multiplier",
    type: "number",
    min: 0.5,
    max: 1.5,
    step: 0.05,
  },

  // Icon
  {
    key: "iconSizeMultiplier",
    label: "Icon Size Multiplier",
    type: "number",
    min: 0.5,
    max: 1.5,
    step: 0.1,
  },
  {
    key: "iconMarginMultiplier",
    label: "Icon Margin Multiplier",
    type: "number",
    min: 0.2,
    max: 0.8,
    step: 0.05,
  },

  // Text
  {
    key: "pillFontWeight",
    label: "Pill Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "pillFontFamily",
    label: "Pill Font Family",
    type: "font",
  },
  { key: "subfixTextColor", label: "Subfix Text Color", type: "color" },
  {
    key: "subfixFontWeight",
    label: "Subfix Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "subfixFontFamily",
    label: "Subfix Font Family",
    type: "font",
  },

  // Container
  {
    key: "containerGap",
    label: "Container Gap",
    type: "number",
    min: 4,
    max: 16,
    step: 1,
  },
];

export const RewardPillWidget: React.FC<RewardPillWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<RewardPillWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve font families - if "inherit", use inherit; otherwise look up from themeFonts
  const pillFontKey = state.pillFontFamily;
  const pillFontData =
    pillFontKey && pillFontKey !== "inherit" ? themeFonts[pillFontKey] : null;

  const subfixFontKey = state.subfixFontFamily;
  const subfixFontData =
    subfixFontKey && subfixFontKey !== "inherit"
      ? themeFonts[subfixFontKey]
      : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const pillGoogleFontLink =
    pillFontData && pillFontData.format === "google" ? pillFontData.url : null;
  const subfixGoogleFontLink =
    subfixFontData && subfixFontData.format === "google"
      ? subfixFontData.url
      : null;

  // Load Google Fonts into document.head when fonts change
  useEffect(() => {
    if (pillGoogleFontLink) {
      loadGoogleFontInHead(pillGoogleFontLink);
    }
  }, [pillGoogleFontLink]);

  useEffect(() => {
    if (subfixGoogleFontLink) {
      loadGoogleFontInHead(subfixGoogleFontLink);
    }
  }, [subfixGoogleFontLink]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  const pillFontFamilyCss = pillFontData
    ? pillFontData.format === "system"
      ? pillFontData.url
      : `"${pillFontData.name}", sans-serif`
    : "inherit";

  const subfixFontFamilyCss = subfixFontData
    ? subfixFontData.format === "system"
      ? subfixFontData.url
      : `"${subfixFontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts)
  const pillNeedsFontFace =
    pillFontData &&
    pillFontData.format !== "system" &&
    pillFontData.format !== "google";
  const subfixNeedsFontFace =
    subfixFontData &&
    subfixFontData.format !== "system" &&
    subfixFontData.format !== "google";

  // Generate unique class for this widget instance
  const uniqueClass = `reward-pill-${widgetId || "default"}`;

  // Generate @font-face CSS and font-family rules
  const styleCss = `
    ${
      pillNeedsFontFace
        ? `
      @font-face {
        font-family: "${pillFontData.name}";
        src: url("${pillFontData.url}") format("${pillFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      subfixNeedsFontFace
        ? `
      @font-face {
        font-family: "${subfixFontData.name}";
        src: url("${subfixFontData.url}") format("${subfixFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      pillFontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-pill-text] { font-family: ${pillFontFamilyCss} !important; }`
        : ""
    }
    ${
      subfixFontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-subfix-text] { font-family: ${subfixFontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: RewardPillWidgetState & {
    pillFontFamilyCss?: string;
    subfixFontFamilyCss?: string;
  } = {
    ...state,
    pillFontFamilyCss,
    subfixFontFamilyCss,
  };

  // Render the children with current state and font styles
  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, resolveColor)}
    </>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
